import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useVideo } from '../context/VideoContext';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Minimize2, Maximize2 } from 'lucide-react';

/**
 * Floating video overlay that persists across page navigation
 * Like Zoom's floating video window
 * Hidden when in Studio (Studio has its own video display)
 */
const VideoOverlay = () => {
  const location = useLocation();
  const {
    connected,
    peerConnected,
    localStream,
    remoteStream,
    micEnabled,
    videoEnabled,
    status,
    roomCode,
    isHost,
    toggleMic,
    toggleVideo,
    leaveRoom
  } = useVideo();
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [minimized, setMinimized] = useState(false);
  
  // Hide overlay when in Studio OR Video Room - these pages have their own video display
  const isInStudio = location.pathname.includes('/studio-session');
  const isInVideoRoom = location.pathname.includes('/video-room');
  
  // Set local video stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);
  
  // Set remote video stream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);
  
  // Don't render if not connected OR if in Studio/VideoRoom (they have their own display)
  if (!connected || isInStudio || isInVideoRoom) {
    return null;
  }
  
  // Minimized view - just a small button
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[9999]">
        <button
          onClick={() => setMinimized(false)}
          className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-full shadow-xl flex items-center gap-2 border border-gray-600"
        >
          <Video className="w-5 h-5" />
          <span className={`w-3 h-3 rounded-full ${peerConnected ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
        </button>
      </div>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-80 bg-gray-900 rounded-xl shadow-2xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${peerConnected ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
          <span className="text-xs text-gray-300 truncate max-w-[150px]">
            {peerConnected ? 'Connected' : status}
          </span>
          <span className="text-xs text-gray-500">| {roomCode}</span>
        </div>
        <button 
          onClick={() => setMinimized(true)} 
          className="p-1 hover:bg-gray-700 rounded"
          title="Minimize"
        >
          <Minimize2 className="w-4 h-4 text-gray-400" />
        </button>
      </div>
      
      {/* Video area */}
      <div className="relative aspect-video bg-black">
        {/* Remote video (main) */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
        
        {/* Waiting overlay when no peer */}
        {!peerConnected && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-3 animate-pulse">
                <Video className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-sm text-gray-300 mb-1">
                {isHost ? 'Waiting for client...' : 'Waiting for host...'}
              </p>
              <p className="text-xs text-gray-500">Room: {roomCode}</p>
            </div>
          </div>
        )}
        
        {/* Local video (small PIP) */}
        <div className="absolute bottom-2 right-2 w-24 aspect-video rounded-lg overflow-hidden border-2 border-gray-600 bg-gray-800 shadow-lg">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[10px] text-center py-0.5 text-gray-300">
            You {isHost ? '(Host)' : '(Client)'}
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="bg-gray-800 px-3 py-2 flex items-center justify-center gap-2">
        <button
          onClick={toggleMic}
          className={`p-2 rounded-full transition-colors ${micEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'}`}
          title={micEnabled ? 'Mute' : 'Unmute'}
        >
          {micEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </button>
        
        <button
          onClick={toggleVideo}
          className={`p-2 rounded-full transition-colors ${videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'}`}
          title={videoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {videoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
        </button>
        
        <button
          onClick={leaveRoom}
          className="p-2 rounded-full bg-red-600 hover:bg-red-500 transition-colors"
          title="Leave room"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default VideoOverlay;
